import cn from 'classnames'
import { NumberInput } from 'legacy/lut/components/common/numberInput/NumberInput'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import React, { useMemo } from 'react'

import { getFormattedFinance } from 'partner360/helpers/getFormattedFinance'

import { getSobPercent, PercentType } from './helpers/getSobPercent'

import styles from './SobCompetitorCell.module.scss'

interface SobCompetitorCellProps {
  yoySob: number | null
  qoqSob?: number | null
  sob: number | null
  sellThru: number | null
  showSellThru?: boolean
  isEditable?: boolean
  onChangeSob?: (value: number) => void
  isTotal?: boolean
  isOtherCompetitor?: boolean
}

export const SobCompetitorCell: React.FC<SobCompetitorCellProps> = ({
  sob,
  sellThru,
  showSellThru = true,
  isEditable,
  onChangeSob,
  yoySob,
  qoqSob,
  isTotal,
  isOtherCompetitor,
}) => {
  const displayedSellThru = useMemo(() => {
    return isOtherCompetitor && sellThru && sellThru < 0 ? null : sellThru
  }, [sellThru, isOtherCompetitor])

  if (isEditable && onChangeSob) {
    return (
      <div className={cn(styles.fields, { [styles.total]: isTotal })}>
        <div className={styles.content}>
          <div className={cn(styles.paragraph, { [styles.paragraph__onlySob]: !showSellThru })}>
            <NumberInput
              value={sob ? String(sob) : ''}
              onChange={(value) => onChangeSob(Number(value))}
              align={'right'}
              max={100}
              min={0}
            />
          </div>
          <div className={cn(styles.paragraph, { [styles.paragraph__onlySob]: !showSellThru })}>
            <p
              className={cn(styles.paragraph, styles.yoyPercent, {
                [styles.negative]: yoySob && yoySob < 0,
                [styles.positive]: yoySob && yoySob > 0,
                [styles.paragraph__onlySob]: !showSellThru,
              })}
            >
              {getSobPercent(yoySob, PercentType.Pt)}
            </p>
          </div>
          <div className={cn(styles.yoyPercent, { [styles.paragraph__onlySob]: !showSellThru })}>
            <p
              className={cn(styles.percent, styles.paragraph, {
                [styles.negative]: qoqSob && qoqSob < 0,
                [styles.positive]: qoqSob && qoqSob > 0,
              })}
            >
              {getSobPercent(qoqSob, PercentType.Pt)}
            </p>
          </div>

          <RenderWhen condition={showSellThru}>
            <p className={cn(styles.paragraph, styles.money)}>
              {getFormattedFinance(displayedSellThru, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </p>
          </RenderWhen>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(styles.fields, {
        [styles.total]: isTotal,
        [styles.warning]: isOtherCompetitor && sob && sob < 0,
      })}
    >
      <div className={styles.content}>
        <p
          className={cn(styles.percent, styles.paragraph, {
            [styles.paragraph__onlySob]: !showSellThru,
          })}
        >
          {getSobPercent(sob)}
        </p>
        <p
          className={cn(styles.paragraph, styles.yoyPercent, {
            [styles.negative]: yoySob && yoySob < 0,
            [styles.positive]: yoySob && yoySob > 0,
            [styles.paragraph__onlySob]: !showSellThru,
          })}
        >
          {getSobPercent(yoySob, PercentType.Pt)}
        </p>
        <p
          className={cn(styles.paragraph, styles.yoyPercent, {
            [styles.negative]: qoqSob && qoqSob < 0,
            [styles.positive]: qoqSob && qoqSob > 0,
          })}
        >
          {getSobPercent(qoqSob, PercentType.Pt)}
        </p>
        <RenderWhen condition={showSellThru}>
          <p className={cn(styles.paragraph, styles.money)}>
            {getFormattedFinance(displayedSellThru, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })}
          </p>
        </RenderWhen>
      </div>
    </div>
  )
}
